/**
 * Synthesis building blocks.
 *
 * Exists so sound work is assembly rather than reinvention, and so every sound in
 * the game shares the same noise and envelope behaviour. Sub-agents should reach
 * for these before writing raw oscillator code.
 *
 * Everything here works in both `AudioContext` and `OfflineAudioContext`.
 */

/**
 * A seamless noise buffer.
 *
 * The last `fadeSamples` are crossfaded with the head, so looping it produces no
 * click. Spec §0 bans abrupt loops, and un-crossfaded noise is the single most
 * common way to violate that.
 */
export function noiseBuffer(
  ctx: BaseAudioContext,
  seconds: number,
  seed = 1,
  fadeSamples = 512,
): AudioBuffer {
  const length = Math.max(Math.floor(ctx.sampleRate * seconds), fadeSamples * 4);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);

  // Deterministic PRNG — reproducible renders mean reproducible measurements.
  let s = seed >>> 0 || 1;
  const rand = (): number => {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return ((s >>> 0) / 0xffffffff) * 2 - 1;
  };

  for (let i = 0; i < length; i++) data[i] = rand();

  // Crossfade the tail into the head so the loop seam is continuous.
  for (let i = 0; i < fadeSamples; i++) {
    const t = i / fadeSamples;
    const head = data[i] ?? 0;
    const tail = data[length - fadeSamples + i] ?? 0;
    data[length - fadeSamples + i] = tail * (1 - t) + head * t;
  }

  return buffer;
}

/** A looping noise source, already started. */
export function noiseSource(
  ctx: BaseAudioContext,
  seconds: number,
  seed = 1,
): AudioBufferSourceNode {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, seconds, seed);
  src.loop = true;
  src.start(0);
  return src;
}

export interface FilterSpec {
  type: BiquadFilterType;
  frequency: number;
  q?: number;
  gain?: number;
}

export function filter(ctx: BaseAudioContext, spec: FilterSpec): BiquadFilterNode {
  const node = ctx.createBiquadFilter();
  node.type = spec.type;
  node.frequency.value = spec.frequency;
  if (spec.q !== undefined) node.Q.value = spec.q;
  if (spec.gain !== undefined) node.gain.value = spec.gain;
  return node;
}

export function gain(ctx: BaseAudioContext, value: number): GainNode {
  const node = ctx.createGain();
  node.gain.value = value;
  return node;
}

/**
 * A slow modulator, e.g. fan wobble or the breathing rate.
 * Returns a running oscillator whose output should be connected to an AudioParam.
 */
export function lfo(
  ctx: BaseAudioContext,
  frequency: number,
  depth: number,
  type: OscillatorType = 'sine',
): OscillatorNode {
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.value = frequency;
  const amp = gain(ctx, depth);
  osc.connect(amp);
  osc.start(0);
  // Caller connects `amp`; expose it on the node for convenience.
  (osc as OscillatorNode & { out?: GainNode }).out = amp;
  return osc;
}

/** The GainNode carrying an lfo's scaled output. */
export function lfoOut(osc: OscillatorNode): GainNode {
  const out = (osc as OscillatorNode & { out?: GainNode }).out;
  if (!out) throw new Error('lfoOut called on a node not created by lfo()');
  return out;
}

/**
 * Fade a node in and out over the render, so a rendered sound never begins or
 * ends on a step. Loops get a very short fade; one-shots get a musical one.
 */
export function envelope(
  ctx: BaseAudioContext,
  duration: number,
  attack = 0.01,
  release = 0.05,
): GainNode {
  const node = ctx.createGain();
  const t0 = ctx.currentTime;
  node.gain.setValueAtTime(0, t0);
  node.gain.linearRampToValueAtTime(1, t0 + attack);
  node.gain.setValueAtTime(1, t0 + Math.max(duration - release, attack));
  node.gain.linearRampToValueAtTime(0, t0 + duration);
  return node;
}

/**
 * Soft saturation, for keeping peaks in check without the pumping of a limiter.
 * Spec §8 requires dynamic range be respected — reach for this before compression.
 */
export function softClip(ctx: BaseAudioContext, amount = 2): WaveShaperNode {
  const shaper = ctx.createWaveShaper();
  const n = 1024;
  const curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * amount) / Math.tanh(amount);
  }
  shaper.curve = curve;
  shaper.oversample = '2x';
  return shaper;
}

/**
 * Connect a chain of nodes in order and return the LAST one.
 *
 * ⚠️ **Do not write `source.connect(chain(a, b))`.** Because this returns the last
 * node, that expression connects `source` straight to `b` and silently orphans
 * `a` — so a gain stage you thought was attenuating is not in the path at all,
 * and the sound plays at full amplitude. This has already caused one clipping bug
 * here. Use `connectChain(source, a, b)` instead, which cannot be misused this way.
 */
export function chain<T extends AudioNode>(...nodes: [AudioNode, ...AudioNode[], T]): T {
  for (let i = 0; i < nodes.length - 1; i++) {
    const from = nodes[i];
    const to = nodes[i + 1];
    if (from && to) from.connect(to);
  }
  return nodes[nodes.length - 1] as T;
}

/**
 * Connect `source` through every node in order, returning the last node.
 * The safe form of `chain` — every node is guaranteed to be in the signal path.
 */
export function connectChain<T extends AudioNode>(
  source: AudioNode,
  ...nodes: [...AudioNode[], T]
): T {
  let current: AudioNode = source;
  for (const node of nodes) {
    current.connect(node);
    current = node;
  }
  return current as T;
}
